import {
  MealInsightControllerService,
  type MealInsightHistoryResponse,
  MealReminderControllerService,
  type MealRemindersPatchRequest,
  MealRemindersResponse
} from "@9amhealth/openapi";
import { parseDate, today as todayInZone } from "@internationalized/date";
import { Blac, Cubit } from "blac-next";
import { onUserSessionReset } from "src/lib/onUserSessionReset";
import reportErrorSentry from "src/lib/reportErrorSentry";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { mealDayKey } from "src/ui/components/MealInsights/mealInsightHelpers";
import {
  buildLogOverview,
  type CalibrationEndChoice,
  countCalibrationDaysLogged,
  DEFAULT_MEAL_REMINDER_SCHEDULE,
  DEFAULT_MEMBER_TIME_ZONE,
  type LogOverviewDay,
  type MealReminderSchedule,
  type MealRemindersPhase,
  mealRemindersPhase,
  remindersPatchFromSchedule,
  scheduleFromReminders,
  uploadDayKey
} from "./mealRemindersHelpers";

/** Covers the calibration week and the strip's past days. */
const HISTORY_WINDOW_DAYS = 7;
const HISTORY_PAGE_SIZE = 200;
/** The member's answer on the day-7 card; it only matters for the rest of day 7. */
const CALIBRATION_END_CHOICE_STORAGE_KEY =
  "meal-reminders-calibration-end-choice";

export interface MealRemindersState {
  schedule: MealReminderSchedule;
  hasSavedSchedule: boolean;
  remindersOn: boolean;
  /** The master toggle; it stays on after a round ends, unlike `remindersOn`. */
  remindersEnabled: boolean;
  phase: MealRemindersPhase;
  logOverview: LogOverviewDay[];
  calibrationDaysLogged: number;
  mealsToday: number;
  loading: boolean;
  loaded: boolean;
}

const buildInitialState = (): MealRemindersState => ({
  schedule: DEFAULT_MEAL_REMINDER_SCHEDULE,
  hasSavedSchedule: false,
  remindersOn: false,
  remindersEnabled: false,
  phase: "notStarted",
  logOverview: [],
  calibrationDaysLogged: 0,
  mealsToday: 0,
  loading: false,
  loaded: false
});

export default class MealRemindersCubit extends Cubit<MealRemindersState> {
  static keepAlive = true;

  private meals: MealInsightHistoryResponse[] = [];
  private firstMealDay?: string;
  private reminders?: MealRemindersResponse;
  /** Days are counted in the member's profile zone, as the backend does for `calibrationCompleted`. */
  private memberTimeZone = DEFAULT_MEMBER_TIME_ZONE;
  /** Bumped on session reset so responses requested for the previous member are dropped. */
  private sessionGeneration = 0;

  constructor() {
    super(buildInitialState());

    onUserSessionReset(() => {
      this.sessionGeneration++;
      this.inFlightLoad = undefined;
      this.reloadRequested = false;
      this.meals = [];
      this.firstMealDay = undefined;
      this.reminders = undefined;
      this.memberTimeZone = DEFAULT_MEMBER_TIME_ZONE;
      this.emit(buildInitialState());
    });
  }

  private readonly memberToday = (): string =>
    todayInZone(this.memberTimeZone).toString();

  private readonly isCurrentSession = (generation: number): boolean =>
    generation === this.sessionGeneration;

  private readonly fetchMemberTimeZone = async (): Promise<string> => {
    const preferences =
      await Blac.getBloc(UserPreferencesCubit).loadUserPreferences();
    return (
      preferences?.[UserPreferenceKeys.timezone] ?? DEFAULT_MEMBER_TIME_ZONE
    );
  };

  private readonly fetchRecentMeals = async (
    timeZone: string
  ): Promise<MealInsightHistoryResponse[]> => {
    const to = todayInZone(timeZone).toString();
    const from = parseDate(to)
      .subtract({ days: HISTORY_WINDOW_DAYS - 1 })
      .toString();
    const response = await MealInsightControllerService.getMealInsightHistory(
      undefined,
      from,
      to,
      undefined,
      0,
      HISTORY_PAGE_SIZE
    );
    return response.data.content ?? [];
  };

  /** Calibration starts on the upload day of the first meal of any status, matching the backend's `calibrationCompleted`. */
  private readonly fetchFirstMealDay = async (
    timeZone: string
  ): Promise<string | undefined> => {
    const response = await MealInsightControllerService.getMealInsightHistory(
      "BOTH",
      undefined,
      undefined,
      undefined,
      0,
      1,
      "createdAt,asc"
    );
    const [firstMeal] = response.data.content ?? [];
    return firstMeal && uploadDayKey(firstMeal.createdAt, timeZone);
  };

  private readonly fetchReminders =
    async (): Promise<MealRemindersResponse> => {
      const response = await MealReminderControllerService.getMealReminders();
      return response.data;
    };

  private readonly calibrationEndChoice = ():
    | CalibrationEndChoice
    | undefined =>
    (StorageController.getItem(
      CALIBRATION_END_CHOICE_STORAGE_KEY
    ) as CalibrationEndChoice | null) ?? undefined;

  private readonly derive = (): void => {
    if (!this.reminders) {
      return;
    }
    const { status, enabled, calibrationCompleted, everEnabled, reminders } =
      this.reminders;
    const today = this.memberToday();
    const loggedDays = new Set(
      this.meals.map((meal) => mealDayKey(meal.takenAt))
    );

    this.patch({
      schedule: scheduleFromReminders(reminders),
      hasSavedSchedule: everEnabled,
      remindersOn: status === MealRemindersResponse.status.ACTIVE,
      remindersEnabled: enabled,
      phase: mealRemindersPhase({
        today,
        startedOn: this.firstMealDay,
        choice: this.calibrationEndChoice(),
        calibrationCompleted
      }),
      logOverview: buildLogOverview(today, loggedDays, this.firstMealDay),
      calibrationDaysLogged: countCalibrationDaysLogged(
        this.firstMealDay,
        today,
        loggedDays
      ),
      mealsToday: this.meals.filter(
        (meal) => mealDayKey(meal.takenAt) === today
      ).length
    });
  };

  private inFlightLoad?: Promise<void>;
  private reloadRequested = false;

  private readonly fetchAndDerive = async (): Promise<void> => {
    const generation = this.sessionGeneration;
    this.patch({ loading: true });
    try {
      do {
        this.reloadRequested = false;
        const timeZone = await this.fetchMemberTimeZone();
        const [meals, firstMealDay, reminders] = await Promise.all([
          this.fetchRecentMeals(timeZone),
          this.fetchFirstMealDay(timeZone),
          this.fetchReminders()
        ]);
        if (!this.isCurrentSession(generation)) {
          return;
        }
        this.memberTimeZone = timeZone;
        this.meals = meals;
        this.firstMealDay = firstMealDay;
        this.reminders = reminders;
        this.derive();
      } while (this.reloadRequested);
    } catch (e) {
      reportErrorSentry(e);
    } finally {
      if (this.isCurrentSession(generation)) {
        this.patch({ loading: false, loaded: true });
      }
    }
  };

  /** A call during a running load fetches again once it ends, so a meal edit mid-load isn't lost. */
  readonly load = (): Promise<void> => {
    if (this.inFlightLoad) {
      this.reloadRequested = true;
      return this.inFlightLoad;
    }
    const load = this.fetchAndDerive().finally(() => {
      if (this.inFlightLoad === load) {
        this.inFlightLoad = undefined;
      }
    });
    this.inFlightLoad = load;
    return load;
  };

  private readonly patchReminders = async (
    request: MealRemindersPatchRequest
  ): Promise<boolean> => {
    const generation = this.sessionGeneration;
    try {
      const response =
        await MealReminderControllerService.patchMealReminders(request);
      if (!this.isCurrentSession(generation)) {
        return false;
      }
      this.reminders = response.data;
      this.derive();
      return true;
    } catch (e) {
      reportErrorSentry(e);
      return false;
    }
  };

  /** Turning reminders on while the last round has ended starts a new 7-day round. */
  readonly saveSchedule = (schedule: MealReminderSchedule): Promise<boolean> =>
    this.patchReminders(remindersPatchFromSchedule(schedule));

  /** Any answer on the day-7 card hides the card for the rest of the day. */
  readonly recordCalibrationEndChoice = (
    choice: CalibrationEndChoice
  ): void => {
    StorageController.setItem(CALIBRATION_END_CHOICE_STORAGE_KEY, choice);
    this.derive();
  };

  readonly keepRemindersOn = async (): Promise<void> => {
    this.recordCalibrationEndChoice("keepOn");
    await this.patchReminders({ enabled: true });
  };

  readonly debugReset = async (): Promise<void> => {
    try {
      await MealReminderControllerService.resetMealReminders();
    } catch {
      // Not reported: outside dev/QA the reset is behind `MEAL_REMINDER_RESET` and 404s.
    }
    StorageController.removeItem(CALIBRATION_END_CHOICE_STORAGE_KEY);
    await this.load();
  };
}
